import { NavLink } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import {
  HomeIcon,
  ArrowUpTrayIcon,
  ArrowUpOnSquareIcon,
  MapIcon,
  ClockIcon,
  Cog6ToothIcon,
  LinkIcon,
  ArrowRightOnRectangleIcon,
  BuildingOfficeIcon,
  UserIcon,
  ChartBarIcon,
  ShoppingBagIcon,
  GlobeAltIcon,
  Squares2X2Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline'

const navigation = [
  { name: 'Get Started', href: '/', icon: HomeIcon },
  { name: 'Dashboard', href: '/dashboard', icon: Squares2X2Icon },
  { name: 'Manual Sync', href: '/manual-sync', icon: ArrowUpOnSquareIcon },
  { name: 'Offline POS', href: '/offline-bridge', icon: GlobeAltIcon },
  { name: 'Import Data', href: '/import', icon: ArrowUpTrayIcon },
  { name: 'Data Mapping', href: '/mapping', icon: MapIcon },
  { name: 'Import History', href: '/history', icon: ClockIcon },
  { name: 'Sync Monitoring', href: '/sync-status', icon: ChartBarIcon },
  { name: 'Marketplace', href: '/marketplace', icon: ShoppingBagIcon },
  { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
]

interface ConnectorSidebarProps {
  /** Drawer state — only meaningful below the `lg` breakpoint. */
  open: boolean
  onClose: () => void
}

/**
 * Connector navigation.
 *
 * The sidebar used to be a fixed 256px column at every viewport width, so on a
 * 375px phone it consumed two thirds of the screen and left the page content
 * squeezed into ~120px. It is now:
 *   - a static column from `lg` up, exactly as before
 *   - an off-canvas drawer below `lg`, opened from the header's menu button and
 *     closed by the scrim, the X, or by navigating
 */
export function ConnectorSidebar({ open, onClose }: ConnectorSidebarProps) {
  const { user, tenant, logout } = useAuth();

  return (
    <>
      {/* Scrim — mobile only, closes the drawer */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-gray-900/50 lg:hidden"
          aria-hidden="true"
          onClick={onClose}
        />
      )}

    <div
      className={`fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-200 flex flex-col transform transition-transform duration-200 ease-out lg:static lg:translate-x-0 lg:z-auto ${open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      aria-label="Main navigation"
    >
      {/* Logo */}
      <div className="p-6 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <LinkIcon className="h-8 w-8 text-primary-600" />
          <div>
            <h1 className="text-lg font-bold text-gray-900">Universal Connector</h1>
            <p className="text-xs text-gray-500">RetailSync</p>
          </div>
        </div>
        {/* Close (mobile only) */}
        <button
          onClick={onClose}
          className="lg:hidden p-1.5 rounded-md text-gray-500 hover:bg-gray-100"
          aria-label="Close navigation"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
        {navigation.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            onClick={onClose}
            className={({ isActive }) =>
              `flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                isActive
                  ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-700'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`
            }
          >
            <item.icon className="mr-3 h-5 w-5" />
            {item.name}
          </NavLink>
        ))}
      </nav>

      {/* Footer with tenant info and logout */}
      <div className="p-4 border-t border-gray-200 space-y-3">
        {/* Tenant context */}
        {tenant && (
          <div className="flex items-center space-x-2 text-xs text-gray-500">
            <BuildingOfficeIcon className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{tenant.name}</span>
          </div>
        )}
        {user && (
          <div className="flex items-center space-x-2 text-xs text-gray-500">
            <UserIcon className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{user.email}</span>
          </div>
        )}

        {/* Logout */}
        <button
          onClick={logout}
          className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-600 hover:bg-red-50 hover:text-red-700 rounded-md transition-colors"
        >
          <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5" />
          Sign Out
        </button>

        <p className="text-xs text-gray-400 text-center pt-1">
          Connect any POS system to RetailSync
        </p>
      </div>
    </div>
    </>
  )
}